import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { adminAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  TruckIcon,
  UsersIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import toast from 'react-hot-toast';

function AdminAnalytics() {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  const { data: reservationAnalytics, isLoading: reservationLoading } = useQuery(
    ['reservation-analytics', dateRange],
    () => adminAPI.getReservationAnalytics(dateRange),
    { select: (response) => response.data }
  );

  const { data: revenueAnalytics, isLoading: revenueLoading } = useQuery(
    ['revenue-analytics', dateRange],
    () => adminAPI.getRevenueAnalytics(dateRange),
    { select: (response) => response.data }
  );

  const { data: operationalAnalytics, isLoading: operationalLoading } = useQuery(
    ['operational-analytics', dateRange],
    () => adminAPI.getOperationalAnalytics(dateRange),
    { select: (response) => response.data }
  );

  const { data: businessIntelligence, isLoading: biLoading } = useQuery(
    ['business-intelligence', dateRange],
    () => adminAPI.getBusinessIntelligence(dateRange),
    { select: (response) => response.data }
  );

  const formatCurrency = (amount: string | number) => {
    const parsed = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
    const safe = Number.isFinite(parsed) ? parsed : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(safe);
  };

  const formatCurrencyFromCents = (amount: string | number | null | undefined) => {
    const parsed = Number(amount ?? 0);
    const safe = Number.isFinite(parsed) ? parsed : 0;
    return formatCurrency(safe / 100);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const revenueChartData = useMemo(() => {
    const daily = revenueAnalytics?.revenueByPeriod?.daily || [];
    return daily.map((point: any) => ({
      date: point.date,
      revenue: Number(point.revenue || 0) / 100,
    }));
  }, [revenueAnalytics]);

  const reservationStatusData = useMemo(() => {
    const distribution = reservationAnalytics?.statusDistribution || {};
    return Object.entries(distribution).map(([name, value], index) => ({
      name,
      value: Number(value || 0),
      color: STATUS_COLORS[index % STATUS_COLORS.length],
    }));
  }, [reservationAnalytics]);

  const totalStatusReservations = useMemo(
    () => reservationStatusData.reduce((sum, s) => sum + s.value, 0),
    [reservationStatusData]
  );

  const exportReport = async (reportType: 'revenue' | 'operational' | 'customer') => {
    try {
      const response = await adminAPI.generateReport({
        reportType,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });

      const payload = response.data;
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportType}-report-${dateRange.startDate}-to-${dateRange.endDate}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`${reportType} report exported`);
    } catch {
      toast.error(`Failed to export ${reportType} report`);
    }
  };

  const isLoading = reservationLoading || revenueLoading || operationalLoading || biLoading;

  return (
    <PageLayout>
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-on-surface">Analytics & Reports</h1>
            <p className="mt-2 text-on-surface-variant">Insights and performance metrics</p>
          </div>
          
          {/* Date Range Picker */}
          <div className="flex items-center space-x-2">
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="input-field"
            />
            <span className="text-on-surface-variant">to</span>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="input-field"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <LoadingSpinner className="py-20" />
      ) : (
        <div className="space-y-8">
          {/* KPI Cards */}
          {businessIntelligence?.kpis && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-on-surface-variant">Monthly Recurring Revenue</p>
                    <p className="text-2xl font-bold text-on-surface">
                      {formatCurrencyFromCents(businessIntelligence.kpis.monthlyRecurringRevenue)}
                    </p>
                  </div>
                  <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="mt-2 flex items-center">
                  <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-on-surface-variant ml-1">
                    {formatPercentage(revenueAnalytics?.revenueGrowth || 0)} period growth
                  </span>
                </div>
              </div>

              <div className="card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-on-surface-variant">Customer Acquisition Cost</p>
                    <p className="text-2xl font-bold text-on-surface">
                      {businessIntelligence.kpis.customerAcquisitionCost == null
                        ? 'N/A'
                        : formatCurrencyFromCents(businessIntelligence.kpis.customerAcquisitionCost)}
                    </p>
                  </div>
                  <UsersIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="mt-2 flex items-center">
                  <ArrowTrendingDownIcon className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-on-surface-variant ml-1">Data source: billing + reservations</span>
                </div>
              </div>

              <div className="card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-on-surface-variant">Customer Lifetime Value</p>
                    <p className="text-2xl font-bold text-on-surface">
                      {businessIntelligence.kpis.customerLifetimeValue == null
                        ? 'N/A'
                        : formatCurrencyFromCents(businessIntelligence.kpis.customerLifetimeValue)}
                    </p>
                  </div>
                  <ChartBarIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div className="mt-2 flex items-center">
                  <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-on-surface-variant ml-1">Calculated from paid invoice history</span>
                </div>
              </div>

              <div className="card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-on-surface-variant">Gross Margin</p>
                    <p className="text-2xl font-bold text-on-surface">
                      {businessIntelligence.kpis.grossMargin == null ? 'N/A' : formatPercentage(businessIntelligence.kpis.grossMargin)}
                    </p>
                  </div>
                  <TruckIcon className="h-8 w-8 text-indigo-600" />
                </div>
                <div className="mt-2">
                  <span className="text-sm text-on-surface-variant">
                    Churn: {businessIntelligence.kpis.churnRate == null ? 'N/A' : formatPercentage(businessIntelligence.kpis.churnRate)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Chart */}
            <div className="card p-6">
              <h3 className="text-lg font-medium text-on-surface mb-4">Revenue Trend</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={revenueChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => new Date(date).toLocaleDateString()}
                  />
                  <YAxis tickFormatter={(value) => `$${value}`} />
                  <Tooltip 
                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                    formatter={(value) => [formatCurrency(Number(value || 0)), 'Revenue']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#3B82F6" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Reservation Status Distribution */}
            <div className="card p-6">
              <h3 className="text-lg font-medium text-on-surface mb-4">Reservation Status Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={reservationStatusData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => {
                      const percent = totalStatusReservations > 0 ? (Number(value) / totalStatusReservations) * 100 : 0;
                      return `${name}: ${percent.toFixed(0)}%`;
                    }}
                  >
                    {reservationStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Reservations']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Performance Metrics */}
          {operationalAnalytics && (
            <div className="card p-6">
              <h3 className="text-lg font-medium text-on-surface mb-6">Operational Performance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center">
                  <p className="text-sm font-medium text-on-surface-variant mb-2">Completion Rate</p>
                  <p className="text-3xl font-bold text-success">
                    {formatPercentage(operationalAnalytics.reservationMetrics.completionRate)}
                  </p>
                  <p className="text-xs text-on-surface-variant mt-1">
                    {operationalAnalytics.reservationMetrics.totalReservations} reservations
                  </p>
                </div>

                <div className="text-center">
                  <p className="text-sm font-medium text-on-surface-variant mb-2">Cancellation Rate</p>
                  <p className="text-3xl font-bold text-info">
                    {formatPercentage(operationalAnalytics.reservationMetrics.cancellationRate)}
                  </p>
                  <p className="text-xs text-on-surface-variant mt-1">
                    Avg duration: {operationalAnalytics.reservationMetrics.averageDuration.toFixed(1)} hrs
                  </p>
                </div>

                <div className="text-center">
                  <p className="text-sm font-medium text-on-surface-variant mb-2">Fleet Utilization</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {formatPercentage(operationalAnalytics.vehicleMetrics.averageUtilization)}
                  </p>
                  <p className="text-xs text-on-surface-variant mt-1">
                    {operationalAnalytics.vehicleMetrics.totalVehicles} vehicles, {operationalAnalytics.vehicleMetrics.maintenanceAlerts} in maintenance
                  </p>
                </div>

                <div className="text-center">
                  <p className="text-sm font-medium text-on-surface-variant mb-2">Customer Retention</p>
                  <p className="text-3xl font-bold text-indigo-600">
                    {formatPercentage(operationalAnalytics.customerMetrics.customerRetentionRate)}
                  </p>
                  <p className="text-xs text-on-surface-variant mt-1">
                    New this period: {operationalAnalytics.customerMetrics.newCustomersThisPeriod}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Business Intelligence Insights */}
          {businessIntelligence && (
            <div className="card p-6">
              <h3 className="text-lg font-medium text-on-surface mb-4">Business Insights</h3>
              {businessIntelligence.insights?.length > 0 ? (
                <div className="space-y-4">
                {businessIntelligence.insights.map((insight: any, index: number) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-surface rounded-card">
                    <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                      insight.impact === 'high' ? 'bg-red-500' : 
                      insight.impact === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                    }`}></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-on-surface capitalize">
                          {insight.title || insight.type}
                        </p>
                        <span className={`px-2 py-1 text-xs font-medium rounded-pill ${
                          insight.impact === 'high' ? 'bg-error-container text-error-on-container' :
                          insight.impact === 'medium' ? 'bg-warning-container text-warning-on-container' :
                          'bg-success-container text-success-on-container'
                        }`}>
                          {insight.impact} impact
                        </span>
                      </div>
                      <p className="text-sm text-on-surface-variant">{insight.description}</p>
                      {insight.actionable && (
                        <p className="text-xs text-orange-600 mt-1">⚠️ Action required</p>
                      )}
                    </div>
                  </div>
                ))}
                </div>
              ) : (
                <p className="text-sm text-on-surface-variant">No prioritized insights were generated for this date range.</p>
              )}
            </div>
          )}

          {/* Revenue Breakdown */}
          {revenueAnalytics && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card p-6">
                <h3 className="text-lg font-medium text-on-surface mb-4">Revenue by Vehicle</h3>
                <div className="space-y-3">
                  {revenueAnalytics.revenueByVehicle?.slice(0, 5).map((vehicle: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-surface rounded-card">
                      <div>
                        <p className="text-sm font-medium text-on-surface">{vehicle.make} {vehicle.model}</p>
                        <p className="text-xs text-on-surface-variant">Vehicle ID: {vehicle.vehicleId}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-on-surface">{formatCurrency(vehicle.revenue)}</p>
                        <p className="text-xs text-on-surface-variant">{formatPercentage(vehicle.utilizationRate)} utilization</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card p-6">
                <h3 className="text-lg font-medium text-on-surface mb-4">Top Customers by Revenue</h3>
                <div className="space-y-3">
                  {revenueAnalytics.topCustomers?.slice(0, 5).map((customer: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-surface rounded-card">
                      <div>
                        <p className="text-sm font-medium text-on-surface">{customer.email || customer.customerId}</p>
                        <p className="text-xs text-on-surface-variant">{customer.reservationCount} reservations</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-on-surface">{formatCurrencyFromCents(customer.totalSpent)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Export Actions */}
          <div className="card p-6">
            <h3 className="text-lg font-medium text-on-surface mb-4">Export Reports</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button className="btn-primary" onClick={() => exportReport('revenue')}>
                Export Revenue Report
              </button>
              <button className="btn-primary" onClick={() => exportReport('operational')}>
                Export Operational Report
              </button>
              <button className="btn-primary" onClick={() => exportReport('customer')}>
                Export Customer Report
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}

const STATUS_COLORS = ['#10B981', '#3B82F6', '#EF4444', '#F59E0B', '#8B5CF6', '#06B6D4'];

export default AdminAnalytics;